import { useEffect, useState, useRef } from 'react';
import { PricingCard } from './PricingCard';
import { CurrencyToggle } from './CurrencyToggle';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import type { Package } from '@/types/subscription';

type Currency = 'NZD' | 'USD';

interface PricingCarouselProps {
  packages: Package[];
  onSelect: (slug: string) => void;
  onSwitchView?: () => void;
  currentPlanSlug?: string;
  selectLabel?: string;
}

export function PricingCarousel({ packages, onSelect, onSwitchView, currentPlanSlug, selectLabel }: PricingCarouselProps) {
  const [currency, setCurrency] = useState<Currency>('NZD');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const popularIndex = packages.findIndex((p) => p.is_popular);
  const [activeIndex, setActiveIndex] = useState(popularIndex >= 0 ? popularIndex : 0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  useEffect(() => {
    if (scrollContainerRef.current && popularIndex >= 0) {
      const container = scrollContainerRef.current;
      const card = container.children[popularIndex] as HTMLElement;
      if (card) {
        const scrollLeft =
          card.offsetLeft - container.clientWidth / 2 + card.clientWidth / 2;
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }
  }, [popularIndex]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 10,
      );
    };

    container.addEventListener('scroll', handleScroll);
    handleScroll();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute('data-index'));
            setActiveIndex(index);
          }
        });
      },
      { root: container, threshold: 0.6 },
    );

    Array.from(container.children).forEach((child) => observer.observe(child));

    return () => {
      container.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  const scrollToIndex = (index: number) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const card = container.children[index] as HTMLElement;
      if (card) {
        const scrollLeft =
          card.offsetLeft - container.clientWidth / 2 + card.clientWidth / 2;
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }
  };

  const scrollBy = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth =
        (container.children[0] as HTMLElement).clientWidth + 24;
      const scrollAmount = direction === 'left' ? -cardWidth : cardWidth;
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8 font-sans relative">
      <div className="flex justify-end mb-8">
        <CurrencyToggle currency={currency} onChange={setCurrency} />
      </div>

      <div className="relative group">
        <button
          onClick={() => scrollBy('left')}
          disabled={!canScrollLeft}
          className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-20 hidden md:flex items-center justify-center w-10 h-10 rounded-full bg-slate-800/80 backdrop-blur border border-slate-700 text-white transition-all ${
            canScrollLeft
              ? 'opacity-0 group-hover:opacity-100 hover:bg-slate-700 hover:scale-110'
              : 'opacity-0 cursor-not-allowed text-slate-600'
          }`}
          aria-label="Scroll left"
        >
          <ChevronLeftIcon className="w-6 h-6" />
        </button>

        <div
          ref={scrollContainerRef}
          className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-8 pt-4 px-[5vw] md:px-[10vw] lg:px-[15vw] embed-hide-scrollbar"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {packages.map((pkg, index) => (
            <div
              key={pkg.id}
              data-index={index}
              className="snap-center shrink-0 w-[85vw] max-w-[320px] md:w-[340px] flex"
            >
              <div className="w-full flex">
                <PricingCard
                  pkg={pkg}
                  currency={currency}
                  delay={index * 0.1}
                  disableScale
                  lowerPlanName={index > 0 ? packages[index - 1].name : undefined}
                  onSelect={onSelect}
                  isCurrentPlan={currentPlanSlug === pkg.slug}
                  selectLabel={selectLabel}
                />
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={() => scrollBy('right')}
          disabled={!canScrollRight}
          className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-20 hidden md:flex items-center justify-center w-10 h-10 rounded-full bg-slate-800/80 backdrop-blur border border-slate-700 text-white transition-all ${
            canScrollRight
              ? 'opacity-0 group-hover:opacity-100 hover:bg-slate-700 hover:scale-110'
              : 'opacity-0 cursor-not-allowed text-slate-600'
          }`}
          aria-label="Scroll right"
        >
          <ChevronRightIcon className="w-6 h-6" />
        </button>
      </div>

      <div className="flex justify-center items-center gap-3 mt-4">
        {packages.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollToIndex(index)}
            className={`rounded-full transition-all duration-300 ${
              activeIndex === index
                ? 'bg-cyan-400 w-2.5 h-2.5'
                : 'bg-slate-700 w-2 h-2 hover:bg-slate-500'
            }`}
            aria-label={`Go to slide ${index + 1}`}
            aria-pressed={activeIndex === index}
          />
        ))}
      </div>

      {onSwitchView && (
        <div className="flex justify-center mt-8">
          <button
            onClick={onSwitchView}
            className="text-sm text-slate-400 hover:text-cyan-400 transition-colors font-medium"
          >
            Not sure which plan? Estimate your usage →
          </button>
        </div>
      )}
    </div>
  );
}
